import { Link } from 'react-router-dom';
import type { NewsArticle } from '@/types/news';
import { formatDateTime, formatRelativeTime, formatViews } from '@/utils/news';
import CategoryTag from '@/components/base/CategoryTag';

interface NewsCardProps {
  article: NewsArticle;
  variant?: 'default' | 'horizontal' | 'compact';
  rank?: number;
}

export default function NewsCard({ article, variant = 'default', rank }: NewsCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-b-0"
      >
        {typeof rank === 'number' ? (
          <span
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
              rank <= 3 ? 'bg-secondary-500 text-background-50' : 'bg-background-200 text-foreground-700'
            }`}
          >
            {rank}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-medium leading-6 text-foreground-900 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
            <span>{formatRelativeTime(article.publishTime)}</span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 sm:flex-row"
      >
        <div className="h-44 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-48">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 爆料网新闻爆料`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2">
            <CategoryTag categoryId={article.category} asLink={false} />
            {article.hot ? (
              <span className="inline-flex items-center gap-0.5 rounded-md bg-secondary-500 px-1.5 py-0.5 text-xs font-medium text-background-50">
                <i className="ri-fire-fill" />
                热点
              </span>
            ) : null}
          </div>
          <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-7 text-foreground-950 group-hover:text-primary-600 transition-colors md:text-lg">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{article.summary}</p>
          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-newspaper-line" />
              {article.source}
            </span>
            <span>{formatDateTime(article.publishTime)}</span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
    >
      <div className="aspect-[3/2] w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 爆料网新闻爆料`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-2">
          <CategoryTag categoryId={article.category} asLink={false} />
          <span className="text-xs text-foreground-500">{formatRelativeTime(article.publishTime)}</span>
        </div>
        <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-7 text-foreground-950 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{article.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-user-line" />
            {article.author}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}